import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunk for fetching dashboard statistics
export const getDashboardStats = createAsyncThunk(
  'stats/getDashboardStats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: {
          Authorization: `Bearer ${auth.userInfo.token}`,
        },
      };

      const { data } = await axios.get('/api/sales/stats', config);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch dashboard statistics'
      );
    }
  }
);

// Async thunk for fetching branch statistics
export const getBranchStats = createAsyncThunk(
  'stats/getBranchStats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: {
          Authorization: `Bearer ${auth.userInfo.token}`,
        },
      };

      const { data } = await axios.get('/api/sales/branch-stats', config);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch branch statistics'
      );
    }
  }
);

// Async thunk for fetching recent activities
export const getRecentActivities = createAsyncThunk(
  'stats/getRecentActivities',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const config = {
        headers: {
          Authorization: `Bearer ${auth.userInfo.token}`,
        },
      };

      const { data } = await axios.get('/api/sales/recent-activities', config);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch recent activities'
      );
    }
  }
);

const statsSlice = createSlice({
  name: 'stats',
  initialState: {
    dashboardStats: {
      todaySales: 0,
      todayProfit: 0,
      todayCount: 0,
      yesterdaySales: 0,
      yesterdayProfit: 0,
      yesterdayCount: 0,
      thisMonthSales: 0,
      thisMonthProfit: 0,
      thisMonthCount: 0,
      lastMonthSales: 0,
      lastMonthProfit: 0,
      lastMonthCount: 0,
    },
    branchStats: {
      katra: {
        todaySales: 0,
        todayProfit: 0,
        thisMonthSales: 0,
        thisMonthProfit: 0,
        topServices: []
      },
      meeratola: {
        todaySales: 0,
        todayProfit: 0,
        thisMonthSales: 0,
        thisMonthProfit: 0,
        topServices: []
      }
    },
    recentActivities: [],
    loading: false,
    branchLoading: false,
    activitiesLoading: false,
    error: null,
  },
  reducers: {
    clearStatsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
        state.error = null;
      })
      .addCase(getDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Branch stats cases
      .addCase(getBranchStats.pending, (state) => {
        state.branchLoading = true;
        state.error = null;
      })
      .addCase(getBranchStats.fulfilled, (state, action) => {
        state.branchLoading = false;
        state.branchStats = action.payload;
        state.error = null;
      })
      .addCase(getBranchStats.rejected, (state, action) => {
        state.branchLoading = false;
        state.error = action.payload;
      })
      // Recent activities cases
      .addCase(getRecentActivities.pending, (state) => {
        state.activitiesLoading = true;
        state.error = null;
      })
      .addCase(getRecentActivities.fulfilled, (state, action) => {
        state.activitiesLoading = false;
        state.recentActivities = action.payload;
        state.error = null;
      })
      .addCase(getRecentActivities.rejected, (state, action) => {
        state.activitiesLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearStatsError } = statsSlice.actions;
export default statsSlice.reducer;
